import React from 'react';
import NewMoneyExchangeRateButtonProperties from "./NewMoneyExchangeRateButtonProperties";
import {useI18nContext} from "../../../contexts/I18nContext";
import NewMoneyExchangeRateStore from "./NewMoneyExchangeRateStore";
import RoundedButton from "../../../ui_components/RoundedButton/RoundedButton";
import Modal from "../../../ui_components/Modal/Modal";
import {Observer} from "mobx-react-lite";
import {useStyles} from "./NewMoneyExchangeRateButton.style";
import Button from "../../../ui_components/Button/Button";
import ButtonType from "../../../ui_components/Button/ButtonType";
import {useCommonContext} from "../../../contexts/CommonContext";
import ButtonSize from "../../../ui_components/Button/ButtonSize";
import TextInput from '../../../ui_components/TextInput/TextInput';
import TextInputType from '../../../ui_components/TextInput/TextInputType';
import Icon from '../../../ui_components/Icon/Icon';
import IconName from '../../../ui_components/Icon/enums/IconName';
import {useUserContext} from "../../../contexts/UserContext";
import Currency from '../../../entities/Currency';
import MoneyExchangeRateRange from '../../../entities/productOrder/moneyDelivery/MoneyExchangeRateRange';
import CurrencySelect from '../../Selects/CurrencySelect/CurrencySelect';

const NewMoneyExchangeRateButton: React.FC<NewMoneyExchangeRateButtonProperties> = ({
                                                                  onClick = () => {
                                                                  },
                                                                  onSave = () => {
                                                                  },
                                                                  showIcon = true,
                                                                  showText = true,
                                                                  disabled,
                                                                  className = '',
                                                                  moneyExchangeRateId,
                                                                  excludedCurrencies = [],
                                                                  agencyIds,
                                                                  rounded = false,
                                                                  size = ButtonSize.DEFAULT,
                                                                  type = ButtonType.DEFAULT
                                                              }) => {
    const classes = useStyles();

    const i18n = useI18nContext();
    const commonContext = useCommonContext();
    const store = new NewMoneyExchangeRateStore(commonContext, moneyExchangeRateId, agencyIds);

    const userStore = useUserContext();
    if (!userStore.isRootAgency()) {
        return <></>;
    }

    const handleOnclick = async () => {
        await store.openModal();
        onClick();
    }

    const onCloseModal = () => {
        store.closeModal();
    }

    const ExchangeRateRangeRow: React.FC<{ 
        range: MoneyExchangeRateRange, 
        index: number,
        canRemove: boolean 
    }> = ({ range, index, canRemove }) => {
        
        const onFromValueChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
            const newFromValue = parseFloat(event.target.value) || 0;
            store.updateExchangeRateRange(index, 'fromValue', newFromValue);
        }
        
        const onToValueChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
            const newToValue = parseFloat(event.target.value) || 0;
            store.updateExchangeRateRange(index, 'toValue', newToValue);
        }

        const onValueChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
            const newValue = parseFloat(event.target.value) || 0;
            store.updateExchangeRateRange(index, 'value', newValue);
        }

        const onRemoveRange = () => {
            store.removeExchangeRateRange(index);
        }

        return (
            <div className={classes.exchange_rate_range_row}>
                <TextInput
                    type={TextInputType.NUMBER}
                    value={range.fromValue}
                    onChange={onFromValueChange}
                    label={i18n.translate("FROM")}
                    className={classes.range_input}
                    fullWidth
                />
                <TextInput
                    type={TextInputType.NUMBER}
                    value={range.toValue}
                    onChange={onToValueChange}
                    label={i18n.translate("TO")}
                    className={classes.range_input}
                    fullWidth
                />
                <TextInput
                    type={TextInputType.NUMBER}
                    value={range.value}
                    onChange={onValueChange}
                    label={i18n.translate("VALUE")}
                    className={classes.range_input}
                    fullWidth
                />
                {canRemove && (
                    <Button
                        onClick={onRemoveRange}
                        type={ButtonType.DANGER}
                        className={classes.remove_range_button}
                        startIcon={<Icon name={IconName.DELETE_ICON}/>}
                        tooltip={"Eliminar"}
                    />
                )}
            </div>
        )
    }

    const onAddRange = () => {
        store.addExchangeRateRange();
    }

    const onExchangeRateSave = async () => {
        if (!store.enableButton) {
            return;
        }
        const saved = await store.save();
        if (saved) {
            store.closeModal();
            if (onSave) {
                onSave();
            }
        }
    }

    const isEditingNames = store.isEditing || (!!agencyIds && agencyIds.length > 1);
    const text = i18n.translate((isEditingNames ? "EDIT_MONEY_EXCHANGES" : "NEW_MONEY_EXCHANGES"));
    const btnIcon = isEditingNames ? <Icon name={IconName.EDIT_ICON}/> : <Icon name={IconName.CREATE_ICON}/>;
    
    const Btn = () => {
        if (rounded) {
            return <RoundedButton
                onClick={handleOnclick}
                className={className}
                disabled={disabled}
                tooltip={!showText ? text : ""}
                text={showText ? text : ""}
                type={type}
                icon={showIcon ? btnIcon : <></>}
            />
        }
        return <Button
            text={showText ? text : ""}
            className={className}
            tooltip={!showText ? text : ""}
            disabled={disabled}
            size={size}
            onClick={handleOnclick}
            type={type}
            startIcon={showIcon ? btnIcon : <></>}
        />
    }

    const onCurrencyChange = (newCurrency: Currency) => {
        store.moneyExchangeRate.currency = newCurrency;
    }

    return <Observer>
        {() => (
            <>
                <Modal 
                    fullScreen={false} 
                    loading={store.loading} 
                    title={text}
                    onClose={onCloseModal}
                    onPressEnter={onExchangeRateSave}
                    open={store.isModalOpen}
                >
                    <div className={classes.modal_container}>
                        <CurrencySelect
                            excludedCurrencies={excludedCurrencies}
                            fullWidth className={classes.input}
                            disabled={store.isEditing}
                            label={'Moneda'}
                            selected={store.moneyExchangeRate.currency}
                            onChange={onCurrencyChange}/>

                        <div className={classes.ranges_header}>
                            RANGOS
                        </div>

                        <div className={classes.exchange_rate_ranges_container}>
                            {store.moneyExchangeRate && 
                                store.moneyExchangeRate?.exchangeRateRanges &&
                                    store.moneyExchangeRate?.exchangeRateRanges.map((range: MoneyExchangeRateRange, i: number) => (
                                        <ExchangeRateRangeRow 
                                            range={range} 
                                            index={i} 
                                            key={i}
                                            canRemove={store.moneyExchangeRate.exchangeRateRanges.length > 1}
                                        />
                            ))}
                        </div>

                        <Button
                            onClick={onAddRange}
                            type={ButtonType.SECONDARY}
                            className={classes.add_range_button}
                            startIcon={<Icon name={IconName.CREATE_ICON}/>}
                            text={i18n.translate("ADD_RANGE")}
                        />

                        <Button
                            disabled={!store.enableButton}
                            loading={store.loading} 
                            className={classes.input} 
                            onClick={onExchangeRateSave}
                            type={ButtonType.PRIMARY}
                            text={i18n.translate("SAVE")}
                        />
                    </div>
                </Modal>
                <Btn/>
            </>
        )}
    </Observer>
}

export default NewMoneyExchangeRateButton;

